// Airtable Client using Vercel Functions for secure API access
// This keeps your API keys safe on the server side

// Get the API endpoint - in production this will be your Vercel deployment
const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT || '/api';

// Helper function to make API requests
async function fetchFromAPI(endpoint, params = {}) {
  const queryString = new URLSearchParams(params).toString();
  const url = `${API_ENDPOINT}${endpoint}${queryString ? `?${queryString}` : ''}`;
  
  try {
    const response = await fetch(url);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || `API request failed: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('API fetch error:', error);
    // Return empty data instead of throwing to allow fallback to static data
    if (endpoint.includes('/articles')) {
      return params.action === 'single' ? null : [];
    }
    throw error;
  }
}

// Fetch all articles
export async function fetchArticles(limit = 20, filterPublished = true) {
  try {
    const articles = await fetchFromAPI('/articles', {
      limit: limit.toString(),
      filterPublished: filterPublished.toString()
    });
    return Array.isArray(articles) ? articles : [];
  } catch (error) {
    console.error('Error fetching articles:', error);
    return [];
  }
}

// Fetch single article by slug
export async function fetchArticleBySlug(slug) {
  if (!slug) return null;
  
  try {
    const article = await fetchFromAPI('/articles', {
      action: 'single',
      slug
    });
    return article;
  } catch (error) {
    console.error('Error fetching article by slug:', error);
    return null;
  }
}

// Fetch articles by category
export async function fetchArticlesByCategory(category, limit = 10) {
  if (!category) return [];
  
  try {
    const articles = await fetchFromAPI('/articles', {
      action: 'category',
      category,
      limit: limit.toString()
    });
    return Array.isArray(articles) ? articles : [];
  } catch (error) {
    console.error('Error fetching articles by category:', error);
    return [];
  }
}

// Fetch featured articles
export async function fetchFeaturedArticles(limit = 3) {
  try {
    const articles = await fetchFromAPI('/articles', {
      action: 'featured',
      limit: limit.toString()
    });
    return Array.isArray(articles) ? articles : [];
  } catch (error) {
    console.error('Error fetching featured articles:', error);
    return [];
  }
}

// Search articles
export async function searchArticles(query, limit = 10) {
  if (!query || query.length < 2) return [];
  
  try {
    const articles = await fetchFromAPI('/articles', {
      action: 'search',
      query,
      limit: limit.toString()
    });
    return Array.isArray(articles) ? articles : [];
  } catch (error) {
    console.error('Error searching articles:', error);
    return [];
  }
}

// Check if Airtable is configured (by testing the API)
export async function isAirtableConfigured() {
  try {
    // Try to fetch just 1 article to test the connection
    const response = await fetch(`${API_ENDPOINT}/articles?limit=1`);
    return response.ok;
  } catch {
    return false;
  }
}